import { useState, useRef, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import DOMPurify from "dompurify";
import {
  BookOpen, Users, Calculator, Shield, FileText, Settings, BarChart3, Search,
  ChevronDown, ChevronRight, Wrench, AlertTriangle, Clock, Upload, Wifi, Bell,
  UserCircle, Rocket, Globe, Layers, UserPlus, KeyRound, MonitorSmartphone,
  Timer, Share2, GitCompare, Mail, TrendingUp, HardDriveDownload, Zap, Info, ArrowRight, Pencil
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

/* ── Section categories for visual grouping ── */
const CATEGORIES = [
  { id: "getting-started", label: "Getting Started", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" },
  { id: "core-workflow", label: "Core Workflow", color: "bg-blue-500/15 text-blue-400 border-blue-500/20" },
  { id: "analysis", label: "Analysis & Reporting", color: "bg-amber-500/15 text-amber-400 border-amber-500/20" },
  { id: "collaboration", label: "Collaboration & Sharing", color: "bg-purple-500/15 text-purple-400 border-purple-500/20" },
  { id: "security", label: "Security & Sessions", color: "bg-rose-500/15 text-rose-400 border-rose-500/20" },
  { id: "configuration", label: "Configuration", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/20" },
  { id: "reference", label: "Technical Reference", color: "bg-slate-500/15 text-slate-400 border-slate-500/20" },
];

const SECTIONS = [
  /* ━━━ GETTING STARTED ━━━ */
  {
    id: "overview",
    icon: BookOpen,
    title: "System Overview",
    category: "getting-started",
    content: `The MEP Pricing Engine is a structured pricing tool for Mechanical, Electrical, and Public Health (MEP) engineering consultancies. It helps you price design consultancy projects using effort-based calculations.

**Core Principle: Effort-Based Pricing**
All fees are derived from the fundamental formula:

Fee = Sum of (Hours per Role x Charge Rate per Role) x Combined Multipliers - Discount

The system never uses arbitrary lump sums. Every fee is traceable back to estimated hours and published rates.

**What the system does:**
- Creates projects with structured setup data (client, sector, type, size metrics)
- Defines scope using pre-built deliverable templates (42 RIBA + 72 BSRIA deliverables across 3 disciplines)
- Calculates fees based on hours, rates, and 6 multiplier layers
- Assesses risk and confidence in pricing accuracy
- Manages approval workflows for fee proposals (7-status pipeline)
- Generates professional client proposals in PDF and Word format
- Tracks pricing history for benchmarking and analytics
- Records timesheets to compare estimated hours vs actual hours
- Supports multi-currency fee presentation (17 currencies)
- Works offline with automatic sync when reconnected (PWA)
- Sends push and email notifications for approval status changes

**Who uses it:**
- **Directors** — Full access (Tier 5). Review, approve, and issue projects. See internal pricing details (cost, margin, health scores). Manage all admin settings including team, rates, and branding.
- **Senior Engineers** — Access level depends on their assigned Permission Tier (see Permission Tiers section). At Tier 4 (Senior Reviewer), they can approve/reject projects and view internal pricing.
- **Engineers** — Access level depends on their assigned Permission Tier. Default is Tier 1 (Timesheet Only). The Director can elevate access as needed.

**Access Control:**
Users are invited by the Director (Admin). There is no self-registration. The Director manages all user accounts, permission tiers, and project assignments via Admin > Team Management.`
  },
  {
    id: "quick-start",
    icon: Zap,
    title: "Quick Start — Your First Project in 5 Minutes",
    category: "getting-started",
    content: `This is the fastest path to pricing your first project.

**Step 1: Log In**
Use the credentials provided by your Director. First-time Directors will see the Onboarding Wizard which guides you through initial setup (branding, rates, deliverables).

**Step 2: Create a Project**
Click "+ New" in the top navigation bar. Fill in:
- Project name and client name
- Select disciplines (Mechanical, Electrical, Public Health)
- Choose sector and project type
- Enter floor area and construction value
- Click "Create Project"

**Step 3: Load Scope**
In the Scope tab, click "Load Template" and select RIBA or BSRIA. This populates your deliverables with default hours. Adjust hours per role as needed for the specific project.

**Step 4: Calculate Price**
Go to the Pricing tab and click "Calculate". The engine multiplies your scope hours by charge rates, applies all configured multipliers, and produces a total fee.

**Step 5: Generate Proposal**
Switch to the Proposals tab and click "Generate Proposal". Download as PDF or Word. Done.

**What happens next:**
- Submit the project for approval (Approval tab)
- A Director reviews and approves or requests revisions
- Once approved, the proposal can be issued to the client
- After the project completes, log actual hours via Timesheets to build benchmarking data`
  },
  {
    id: "onboarding",
    icon: Rocket,
    title: "Onboarding & First-Time Setup",
    category: "getting-started",
    content: `When the first Director logs in and the platform has not been configured, they are automatically redirected to the Onboarding Wizard. This 4-step setup ensures the platform is ready for use.

**Step 1: Branding**
Configure the platform identity:
- Platform name and suffix
- Company/owner name
- Tagline for the login screen
- Upload a company logo (appears in navigation and proposals)
- Toggle owner name visibility in the navigation bar

**Step 2: Charge Rates**
Set the hourly charge rates and internal cost rates for each role. The wizard pre-fills the 5 default roles:
- Director, Associate, Senior Engineer, Engineer, CAD/BIM Technician

Adjust the rates to match your consultancy's current fee schedule. You can add additional roles later in Admin > Charge Rates.

**Step 3: Deliverables Library**
Import your deliverables library. Three options:
- **Load RIBA Defaults** — Import the built-in 42 RIBA deliverables (14 per discipline)
- **Load BSRIA Defaults** — Import the built-in 72 BSRIA deliverables (24 per discipline)
- **CSV Import** — Upload a custom deliverables CSV with your own library

The CSV format requires columns: name, discipline, stage, description, and optional default hours per role.

**Step 4: Launch**
Review your configuration and click "Complete Setup" to finish onboarding. The platform redirects to the Dashboard, and the onboarding wizard will not appear again.

**After Onboarding**
All settings configured during onboarding can be changed at any time in Admin Settings. Onboarding simply provides a guided first-time experience.`
  },
  /* ━━━ CORE WORKFLOW ━━━ */
  {
    id: "projects",
    icon: FileText,
    title: "Creating a Project",
    category: "core-workflow",
    content: `**Step 1: Project Info**
Enter the project name, client name, and internal reference number. Select the disciplines to include (Mechanical, Electrical, Public Health — or a subset). Choose the stage framework (RIBA is default, BSRIA also available). If templates are available, select one to pre-fill details.

**Step 2: Location & Type**
Set the location, sector (Commercial, Residential, Healthcare, etc.), project type (New Build, Refurbishment, Fit Out), and procurement route (Traditional, Design and Build, etc.). Enter the estimated construction value — this is used for benchmarking.

**Modelling Method (CAD vs BIM)**
Choose whether the project uses 2D CAD or BIM. This is important because:
- **BIM projects** automatically include BIM-related effort drivers (e.g. BIM Level 2 Requirement at 1.08x)
- **2D CAD projects** exclude all BIM-related multipliers from the pricing calculation
This ensures you are not overpricing projects that do not require BIM deliverables.

**Step 3: Size Metrics**
Enter the physical size parameters: floor area (sqm), number of floors, residential units, plant rooms, and risers. Select the relevant technical systems from the grouped multi-select panel — systems are organised by Mechanical, Electrical, and Public Health categories. Click on a system to toggle it on/off; selected systems show a blue highlight and checkmark. The count of selected systems feeds into the size scaling multiplier.

**Step 4: Review & Create**
Confirm all details and create the project. The project starts in Draft status.

**Quick Price Guidance**
Once you enter the sector and floor area, the sidebar shows an indicative fee range based on historical project data. This is guidance only — it does not override the effort-based calculation.

**Project Duplication**
From any project's Overview tab, click the Duplicate button to create a copy of the project with all scope, pricing factors, and risk settings carried over. The duplicate starts in Draft status with a fresh audit trail.

**Batch Delete**
On the Project List screen, select multiple projects using the checkboxes and use the batch delete action. This permanently removes all selected projects and their associated data. Only Directors can batch delete.`
  },
  {
    id: "scope",
    icon: Settings,
    title: "Scope Builder",
    category: "core-workflow",
    content: `The Scope tab defines what deliverables are included in the project.

**Loading Deliverables**
Click "Load Template" to populate the scope with deliverables from the library. Two frameworks are available:
- **RIBA** — 42 deliverables across Mechanical, Electrical, and Public Health disciplines, covering Stages 2-6
- **BSRIA** — 72 deliverables across the same disciplines, covering Phases 1-6

Each deliverable includes:
- Stage (e.g. Stage 2 Concept Design, Phase 3 Scheme Design)
- Discipline (Mechanical, Electrical, Public Health)
- Description
- Default estimated hours by role (the role columns are dynamic — they reflect whatever roles are configured in Admin > Charge Rates)

**Editing Scope**
- Toggle deliverables on/off using the checkbox. Turning off an item automatically generates an exclusion
- Edit hour estimates per role for each deliverable directly in the table
- All number cells use smart editing: click a cell to auto-select the entire value, type your new number (it replaces the old value), then click away or press Enter to save. Press Escape to revert. This applies to every number input across the entire application.
- Add optional services that sit outside the base scope (priced separately)

**Hours vs Days Toggle**
Each deliverable can be priced in either Hours or Days. This is controlled at two levels:

1. **Stage-level toggle** — In the stage header, click "Hours" or "Days" to switch all deliverables in that stage to the selected unit. When you switch from Hours to Days, all cell values are automatically divided by the configured Hours per Day value. Switching back multiplies them.

2. **Per-deliverable toggle** — Each row has a unit badge in the "Unit" column showing "hrs" or "days". Click the badge to toggle that individual deliverable between hours and days. The cell values convert automatically.

Example: If Hours per Day is set to 8 and a cell shows 12 hrs, switching to Days converts it to 1.5 days. Switching back gives 12 hrs.

The Hours per Day conversion rate is configured in Admin > General Settings (default: 8). All pricing calculations convert days back to hours internally before applying charge rates.

**Deleting a Stage**
You can delete an entire stage from the scope using the delete button on the stage header. This removes all deliverables in that stage and automatically generates exclusions for each removed item.

**CSV Import**
You can import deliverables via CSV file. The CSV must include columns for name, discipline, stage, description, and default hours. This is useful when migrating existing scope data or importing from external tools. CSV import is available in Admin > Deliverables Library and during the Onboarding Wizard. See the "CSV Scope Import, Export & Templates" section for detailed format and workflow.

**Staff Hours CSV**
For large projects, you can download a pre-populated hours template CSV, distribute it to discipline leads, and upload it back with completed hours. See the "Staff Hours CSV Import & Export" section for the full workflow.

**General Discipline**
Deliverables can be assigned to the "General" discipline for cross-discipline activities like project management, coordination, or site visits. General items are fully integrated into pricing — see the "General / Cross-Discipline Items" section.

**Exclusions & Assumptions**
- **Exclusions** are auto-generated when scope items are removed or a stage is deleted. They describe what is NOT included in the fee.
- **Assumptions** are generated when information is missing. They describe what has been assumed for pricing purposes.
- Both are included in client proposals.

**How Scope Feeds Into Pricing**
Total hours per role = Sum of all included deliverable hours for that role.
These hours are then multiplied by charge rates and multipliers in the Pricing tab.`
  },
  {
    id: "staff-levels",
    icon: Users,
    title: "Per-Project Staff Levels",
    category: "core-workflow",
    content: `Each project has its own set of active engineering roles (staff levels), independent of the global admin rates.

**How It Works**
When a new project is created, it inherits all active roles from Admin > Charge Rates as its default staff levels. You can then customise which roles are active for each specific project.

**Managing Staff Levels**
On the Project Overview tab, the Staff Levels panel shows all active roles for the project. Click "Manage" to enter edit mode:

- **Remove roles** — Click the trash icon to deactivate a role you don't need for this project. Hours already allocated to that role are preserved but hidden from the scope builder and pricing.
- **Add admin roles** — Quick-add buttons appear for any admin roles not currently in the project.
- **Add custom roles** — Type a custom role name (e.g. "Contractor - Hourly", "Acoustic Consultant") and click Add. If the name matches an admin role, rates are pulled automatically.
- **Reorder roles** — Use the up/down arrows to control the column order in the scope builder.
- **Edit rates** — Click the pencil icon to open the inline rate editor for any role. Set a custom charge rate and/or cost rate for this project only.

**Custom Rate Overrides**
By default, roles use the admin-configured charge and cost rates. You can override these per project:

- An amber "Override" badge replaces the blue "Admin" badge when a role has custom rates
- The rate editor shows the admin rate for reference (e.g. "Admin: £185")
- Click "Reset to Admin" to clear the override and revert to admin rates
- Custom rates are used in the pricing calculation for that project only

**Rate Change Impact Preview**
When editing a rate, a live preview shows the fee impact before saving:
- Current Fee (based on existing rate x scoped hours)
- New Fee (based on the rate you're entering)
- Difference (green for increase, red for decrease)

This only appears when the role has hours allocated in the scope.

**Proposal Staleness**
When you save a rate override and proposals already exist, a warning toast appears. The Proposals tab shows an amber banner: "Rates Changed Since Last Proposal" — prompting you to recalculate pricing and regenerate. Generating a new proposal clears the flag.

**Effect on Pricing**
The scope builder columns, pricing calculations, pricing requests, and timesheets all dynamically reflect the project's active staff levels and custom rates — not the global admin defaults.`
  },
  {
    id: "pricing",
    icon: Calculator,
    title: "Pricing Engine — Full Calculation Breakdown",
    category: "core-workflow",
    content: `This section explains every step of the pricing calculation.

**Step 1: Gather Base Hours**
For each included deliverable in the scope, the system sums the hours by role:

Total Hours (per role) = Sum of that role's hours across all included deliverables

If a deliverable uses "days" as its effort unit, the system converts: hours = days x hours_per_day (configurable in Admin > General Settings, default 8).

Roles are configurable via Admin > Charge Rates. The default roles are Director, Associate, Senior Engineer, Engineer, and CAD/BIM Technician. Directors can add, archive, or delete roles. New roles appear automatically in the scope builder and pricing calculations.

**Step 2: Apply Charge Rates**
Each role has a charge rate (fee to client) and a cost rate (internal cost). These are configured in Admin > Charge Rates.

Base Fee per Role = Total Hours x Charge Rate
Base Cost per Role = Total Hours x Cost Rate

Subtotal Fee = Sum of all Base Fee per Role
Subtotal Cost = Sum of all Base Cost per Role

**Step 3: Calculate Multipliers**
Six multiplier layers are applied to the subtotal:

1. **Complexity Factor** (default 1.0) — Adjusts for project technical difficulty. Set manually per project. A factor of 1.2 means the project is 20% more complex than typical.

2. **Coordination Factor** (default 1.0) — Accounts for multi-discipline coordination effort. Higher values for projects requiring extensive cross-discipline coordination.

3. **Programme Duration** — Select from Normal, Compressed, Accelerated, or Extended. Adjusts fees for time pressure on the design programme (e.g. Accelerated = 1.25x).

4. **Size Scaling Multiplier** — Calculated automatically from project size metrics using a threshold-based model:

   **Floor area base multiplier** (lookup table):
   - Up to 1,000 sqm = 0.85x (small project discount)
   - Up to 5,000 sqm = 1.00x (standard baseline)
   - Up to 15,000 sqm = 1.10x
   - Up to 50,000 sqm = 1.20x
   - Over 50,000 sqm = 1.35x

   **Additive bonuses** (applied only above minimum thresholds):
   - Floors above 5: (floors - 5) x 0.02 per additional floor
   - Residential units above 20: (units - 20) x 0.005 per additional unit
   - Plant rooms above 2: (plant_rooms - 2) x 0.03 per additional room
   - Risers above 2: (risers - 2) x 0.01 per additional riser
   - Technical systems above 5: (systems - 5) x 0.02 per additional system

   Size Multiplier = floor area base multiplier + sum of applicable bonuses

   All threshold values and bonus coefficients are configurable in Admin > General Settings (size_scaling_rules). The floor area uses step thresholds, not a continuous formula. Bonuses only apply when the metric exceeds the minimum threshold for that category.

5. **Procurement Route Effect** — Configured in Admin > General Settings. Each procurement route has a multiplier:
   Traditional = 1.0x (standard)
   Design and Build = 0.9x (reduced scope typically)
   Construction Management = 1.1x (more coordination)
   Two-stage tender = 1.05x

6. **Effort Drivers** — Compound multipliers configured in Admin > General Settings. Each active driver multiplies the fee. For example:
   BIM Level 2 Requirement = 1.08x
   Listed Building / Heritage = 1.15x
   BREEAM Excellent Target = 1.10x

If all three are active: Effort Driver Multiplier = 1.08 x 1.15 x 1.10 = 1.3662x

**Important:** If the project modelling method is set to "2D CAD", all BIM-related effort drivers are automatically excluded.

Effort Driver Multiplier = Product of all applicable driver multipliers

**Step 4: Calculate Combined Multiplier**
Combined Multiplier = Complexity x Coordination x Size Scaling x Procurement x Programme Duration x Effort Drivers

**Step 5: Apply to Fee**
Adjusted Fee = Subtotal Fee x Combined Multiplier
Adjusted Cost = Subtotal Cost x Combined Multiplier

**Step 6: Confidence Contingency**
The system applies a contingency based on the project's confidence scoring (from the Risk tab):

- **Low Confidence** (average below 2.0): +10% uplift on the Adjusted Fee
- **Medium Confidence** (average 2.0 to 3.5): +5% uplift
- **High Confidence** (average above 3.5): +0% (no uplift)

Fee After Contingency = Adjusted Fee + (Adjusted Fee x Contingency %)

This protects against pricing uncertainty when project information is incomplete. The contingency percentages are configurable in Admin > General Settings (confidence_contingency).

**Step 7: Apply Discount**
Discount Amount = Fee After Contingency x (Discount Percentage / 100)
Final Fee = Fee After Contingency - Discount Amount

**Discount Governance:** If the discount exceeds the admin-configured threshold (default 15%), the project is automatically flagged for approval and the health score reflects a warning.

**Step 8: Fee Rounding (Optional)**
If fee rounding is configured, the Final Fee is rounded up (ceiling) to the nearest specified increment. For example, if rounding is set to 500, a fee of 12,340 becomes 12,500.

**Step 9: Calculate Margin (Directors Only)**
Margin = Final Fee - Adjusted Cost
Margin % = (Margin / Final Fee) x 100

**Step 10: Benchmark Check**
Benchmark % = (Final Fee / Estimated Construction Value) x 100
This is compared against the target benchmark percentage (configured in Admin > General Settings, typically 3-5% for MEP).

**Step 11: Fee Breakdown**
Fees are also broken down by:
- **Stage** — how much per RIBA/BSRIA stage, with fee and hours percentages
- **Discipline** — how much per Mechanical, Electrical, Public Health
- **Role** — how much per Director, Associate, etc.

**Optional Services**
Optional services are priced separately and shown as add-on items. Their hours and fees are not included in the base fee total but are shown for client selection. The combined multiplier is still applied to optional service fees.

**Contractors / Sub-Consultants**
Contractors are added as pass-through cost items in the Pricing tab. Each contractor has:
- **Cost** — what you pay the contractor
- **Markup toggle** — when enabled, a markup percentage is applied to calculate the client-facing fee
- **Client Fee** — calculated as Cost x (1 + Markup% / 100) when markup is on, or equal to Cost when off

Contractor costs are separate from the effort-based fee calculation. The Project Total (incl. Contractors) adds the contractor client fees to the pricing engine total.

**Contractor Sources:**
- **From Library** — select a contractor from the Admin > Charge Rates > Contractor Library to pre-fill name and rates
- **Ad-hoc** — type a contractor name and fee manually for one-off entries

Only Directors can see the internal cost vs client fee margin on contractors.`
  },
  {
    id: "scenarios",
    icon: GitCompare,
    title: "Scenario Comparison",
    category: "core-workflow",
    content: `The Scenario Comparison feature allows you to run side-by-side what-if analyses on your pricing without changing the saved calculation.

**How to Use Scenario Comparison**
In the Pricing tab, click "Compare Scenarios" to open the comparison panel. You can create multiple scenarios, each with different pricing inputs:

- Complexity factor
- Coordination factor
- Discount percentage
- Programme duration
- Procurement route effect
- Active effort drivers
- Size scaling on/off

**What You See**
Each scenario column shows:
- Total hours
- Subtotal fee
- Combined multiplier
- Discount amount
- Final fee
- Margin and margin percentage (Directors only)

A **"vs Base" delta column** highlights the difference between each scenario and your current saved pricing, making it easy to see the impact of each change.

**Use Cases**
- Compare a 10% discount vs a 15% discount
- See the fee impact of switching from Traditional to Design & Build procurement
- Test whether adding BIM Level 2 effort driver justifies the additional cost
- Show the client three pricing options (standard, reduced scope, enhanced scope)

**Important:** Scenario comparison is read-only — it does not save any changes to your project pricing. To apply a scenario, manually update the pricing factors on the main Pricing tab and recalculate.`
  },
  {
    id: "risk",
    icon: Shield,
    title: "Risk & Confidence Scoring",
    category: "core-workflow",
    content: `The Risk tab assesses the reliability of your pricing basis.

**Confidence Scoring (0 to 5 scale)**
Rate four factors on a scale of 0 (no information) to 5 (complete information):

- **Drawings** — Are design drawings available and clear?
- **Specifications** — Are technical specifications provided?
- **Surveys** — Have site surveys been completed?
- **Programme Clarity** — Is the project programme defined?

**Confidence Band Calculation:**
Average Score = (Drawings + Specifications + Surveys + Programme Clarity) / 4

- **High Confidence** (average 3.5 or above) — Good pricing basis. Fee estimate is reliable. No contingency applied.
- **Medium Confidence** (average 2.0 to 3.5) — Some uncertainty. +5% contingency applied to fee.
- **Low Confidence** (below 2.0) — Significant pricing risk. +10% contingency applied to fee.

**How Confidence Affects Pricing:**
The confidence band directly influences the fee calculation. A confidence contingency multiplier is applied after the adjusted fee and before discount. This means completing the confidence assessment accurately is important — higher confidence reduces the automatic uplift, producing a more competitive fee. Projects with no risk assessment default to Low confidence (+10%).

The contingency percentages are configurable in Admin > General Settings (confidence_contingency).

**Three-Point Estimates (PERT)**
For each deliverable group, you can enter three hour estimates:
- **Optimistic** (O) — Best case hours
- **Most Likely** (M) — Expected hours
- **Pessimistic** (P) — Worst case hours

The PERT formula calculates a weighted estimate:
PERT Estimate = (O + 4M + P) / 6

Standard Deviation = (P - O) / 6

This gives a statistically weighted estimate that accounts for uncertainty. The PERT estimate is typically slightly higher than the most likely, reflecting the natural skew of project risk.

**Risk Register**
Add specific risk items in five categories:
1. **Scope Risk** — Risk of scope change, undefined requirements
2. **Programme Risk** — Risk of compressed timelines, delays
3. **Coordination Risk** — Risk from multi-trade interfaces
4. **Existing Services Risk** — Risk from unknown existing conditions
5. **Stakeholder Risk** — Risk from multiple decision-makers

Each risk has:
- A probability level (Low / Medium / High)
- Impact hours (additional hours if the risk materialises)

**Risk to Hours Conversion**
Total Risk Hours = Sum of all Impact Hours across all risk items.

The probability level (Low / Medium / High) is stored as descriptive metadata for each risk and is displayed in the risk register, but it does not weight or reduce the impact hours. Every risk item contributes its full impact hours to the total.

Risk hours are then distributed into the base hour pool as follows:
- 40% allocated to Senior Engineer
- 60% allocated to Engineer

These additional hours are priced at the respective charge rates and are included in the base fee before multipliers are applied.

**Risk Narrative**
The client-facing risk narrative appears in proposals. Internal risk pricing values (impact hours, risk categories) are never shown to clients.`
  },
  {
    id: "approval",
    icon: Users,
    title: "Approval Workflow",
    category: "core-workflow",
    content: `Projects follow a 7-status approval pipeline:

**Draft** — Initial state. Project is being set up and priced.
**Ready for Review** — Senior Engineer submits for review.
**Under Review** — Director starts formal review.
**Revision Required** — Director requests changes (returns to team).
**Approved** — Director approves the pricing and proposal.
**Rejected** — Director rejects (with reason).
**Issued** — Proposal sent to client.
**Closed** — Project complete.

**Who can do what (by Permission Tier):**
- **Tier 3 (Project Lead)** can: Submit for review (Draft to Ready for Review)
- **Tier 4 (Senior Reviewer)** can: Approve, reject, and request revision on assigned projects
- **Tier 5 (Director)** can: Start review, approve, reject, request revision, issue, close, and force-approve at any stage
- **Tier 1 and Tier 2** users cannot access the Approval tab

**Note:** The Approvals queue page (accessible from the navigation bar) is restricted to Tier 4 and above. Users below Tier 4 who attempt to access /approvals via URL will be redirected to the Dashboard.

**Reapproval Triggers**
If an approved project has changes to any of these items, it automatically returns to Draft and requires fresh approval:
- Fee value (pricing recalculation)
- Scope items (added/removed deliverables)
- Exclusions or assumptions
- Discount percentage
- Risk hours

**Notifications**
Status changes trigger three types of notification:

1. **In-app notifications** — Visible via the bell icon in the navigation bar. A red badge shows the count of unread notifications. Click to view, and mark as read individually or all at once.

2. **Push notifications** — If the user has enabled push notifications on their device (via My Profile), they receive browser push alerts for status changes. This works even when the app tab is not in focus.

3. **Email notifications** — If email is configured in Admin > Email, the system sends email notifications for status changes. Each event type can be individually enabled or disabled.

**Approvals Queue**
Directors can view all projects awaiting approval from the dedicated Approvals screen, accessible from the navigation bar.`
  },
  {
    id: "proposals",
    icon: FileText,
    title: "Proposal Generation",
    category: "core-workflow",
    content: `The Proposals tab generates professional client documents.

**Generating a Proposal**
Click "Generate Proposal" to create a new version. Each proposal includes:
- Project overview (client, location, sector, type)
- Scope of services (all included deliverables listed by stage)
- Deliverables by stage (with descriptions)
- Assumptions (what has been assumed)
- Exclusions (what is not included)
- Fee summary — presented by both stage and discipline
- Optional services (as selectable priced items)
- Risk narrative (client-facing risk description)

**Important:** Internal pricing details (cost, margin, individual role rates) are NEVER included in client proposals.

**Customisation**
Edit the introduction and closing text for each version. Toggle the DRAFT watermark on or off. The branding (company name, platform name) configured in Admin > Branding appears on generated proposals.

**Download Formats**
Each proposal version has PDF and Word download buttons. Word documents can be further edited externally before sending.

**Version History**
All proposals are stored with version numbers (v1, v2, v3...) and issue dates. Previous versions remain accessible. This provides a full revision history.

**Deleting Proposals**
Individual proposal versions can be deleted if no longer needed. Deletion is recorded in the audit trail.`
  },
  /* ━━━ COLLABORATION & SHARING ━━━ */
  {
    id: "share-links",
    icon: Share2,
    title: "Shared Proposal Links",
    category: "collaboration",
    content: `You can share proposals with clients via a read-only public link, without requiring them to log into the system.

**Creating a Share Link**
1. Go to the Proposals tab on any project
2. Next to the proposal version you want to share, click the "Share" button
3. A unique URL is generated (e.g. /shared/proposal/abc123def456)
4. Copy the link and send it to your client via email or any communication channel

**Link Properties**
- **Read-only** — Clients can view the proposal but cannot edit anything
- **30-day expiry** — Links automatically expire after 30 days. The expiry date is shown when you create the link.
- **No login required** — The client does not need an account to view the proposal

**View Tracking**
Each share link tracks:
- Total view count (how many times the link was accessed)
- IP address of viewers
- Device and browser information
- Timestamp of each view

This tracking data is visible to the project team but not to the client.

**Managing Share Links**
- View all active share links for a proposal from the Proposals tab
- **Revoke** a link instantly if needed — revoked links return a "Link expired" message
- Create multiple share links for the same proposal version if needed (e.g. for different client contacts)

**Security**
Share links contain a unique random token. They cannot be guessed. Revoking a link is immediate and irreversible — a new link must be created if sharing is needed again.`
  },
  {
    id: "timesheets",
    icon: Clock,
    title: "Timesheets",
    category: "collaboration",
    content: `The Timesheets tab on each project records actual hours worked, enabling comparison against the estimated scope hours.

**Why Timesheets Matter**
Timesheets close the feedback loop between estimates and reality. By recording actual hours per project, you can:
- Compare estimated hours vs actual hours by stage, discipline, and role
- Identify which project types or stages are consistently under- or over-estimated
- Build more accurate pricing data for future projects
- Feed data into the Benchmarking and Pricing Intelligence dashboards

**Logging Hours**
Two methods are available:

1. **Single Entry** — Click "Log Hours" to record a single time entry. Enter:
   - Date
   - Hours (in 0.25h increments)
   - Stage (optional — maps to scope stages)
   - Discipline (optional — Mechanical, Electrical, Public Health)
   - Role (matches configured roles)
   - Note (brief task description)

2. **Quick Week Entry** — Click "Week Entry" to open a Monday-to-Friday grid. Set default stage, discipline, and role, then enter hours for each day. This is faster for recording a full week.

**Duplicating Previous Days**
Quick-access buttons appear at the top of the entries list, allowing you to duplicate all entries from a previous date. This is useful when tasks repeat across days.

**Submission Workflow**
Timesheet entries follow a weekly approval cycle:
- **Draft** — Entries are editable and not yet submitted
- **Submitted** — The user submits their week for approval. Entries become locked for editing.
- **Approved** — A Director or Senior Engineer approves the week. Entries are permanently locked and included in analytics.
- **Rejected** — The reviewer rejects the week with a comment. Entries are unlocked for correction.

To submit a week, click "Submit Week" at the bottom of the entries list when you have Draft entries for the current week.

**Director Controls**
Directors can:
- View all team members' entries across the project
- Approve or reject submitted weeks (with optional comments)
- Unlock previously approved weeks if corrections are needed (Director override)
- Edit any entry regardless of ownership

**Summary View**
The Summary tab provides visual analytics:
- **Hours by Stage** — Bar chart comparing estimated vs actual hours per stage
- **By Discipline** — Horizontal bar chart showing estimated vs actual per discipline
- **By Role** — Breakdown by team role
- **By Team Member** — Hours contributed by each person

A progress bar shows overall completion (actual / estimated), with colour coding:
- Green: Under 80% — on track
- Amber: 80-100% — approaching estimate
- Red: Over 100% — exceeded estimate

The totals bar shows Estimated, Actual, Approved, and Variance (actual minus estimated) at a glance.`
  },
  {
    id: "files",
    icon: Upload,
    title: "File Uploads",
    category: "collaboration",
    content: `The Files tab on each project stores supporting documents.

**Uploading Files**
Click the upload button to attach files to the project. Supported files include:
- Design drawings (PDF, DWG, DXF)
- Technical specifications
- Site survey reports
- Project programmes
- Meeting minutes
- Any other supporting documentation

Files are stored in cloud object storage and are available to all team members with access to the project.

**Managing Files**
- View a list of all uploaded files with file name, upload date, and file size
- Download any file directly from the list
- Delete files that are no longer needed (deletion is recorded in the audit trail)

**Best Practice**
Upload key reference documents early in the project lifecycle. The Confidence Scoring in the Risk tab considers the availability of drawings, specifications, and surveys — having these uploaded helps justify higher confidence scores during pricing review.`
  },
  {
    id: "team-assignment",
    icon: UserPlus,
    title: "Team Assignment",
    category: "collaboration",
    content: `Team assignment controls which users have access to which projects. The system supports two-way assignment — you can assign projects to users, or assign users to projects.

**Method 1: User-Centric Assignment (Admin > Team Management)**
This is the primary method for bulk-assigning projects to a user.

1. Go to Admin > Team Management
2. Click on a user row to expand the project assignment panel
3. The panel shows all projects with checkboxes
4. Use "Select All" or "Clear" to toggle all projects
5. Use the search filter to find specific projects by name
6. Tick the projects the user should have access to
7. Click "Assign N Projects" to save

This method is efficient when onboarding a new team member or when a user's project portfolio changes.

**Method 2: Project-Centric Assignment (Project Overview Tab)**
This method is used when you want to manage the team for a specific project.

1. Open the project and go to the Overview tab
2. Click the "Manage" button in the Team section
3. An inline team editor opens with a type-to-search dropdown
4. The dropdown shows all available users with their role and tier badges (e.g. "Jane Smith — Senior Engineer — Tier 3")
5. Select users to add to the project team
6. Remove users by clicking the remove button next to their name
7. Changes save automatically

This method is efficient when setting up a new project team or adjusting team composition.

**Bidirectional Sync:**
Both methods update the same underlying data. When you assign a project to a user via Team Management, that user appears in the project's team list. When you add a user to a project via the Overview tab, that project appears in the user's assigned projects. The sync is immediate and automatic.

**How Assignment Affects Access:**
- Non-Director users can only see projects they are assigned to in the project list and dashboard
- Directors always see all projects regardless of assignment
- Unassigned users who attempt to navigate directly to a project URL will be redirected to the Dashboard
- Assignment does not override permission tiers — a Tier 1 user assigned to a project can still only access the Timesheets tab for that project

**Audit Trail:**
All assignment changes are logged in the audit trail, recording who made the change, which user was affected, which project, and the timestamp.`
  },
  /* ━━━ ANALYSIS & REPORTING ━━━ */
  {
    id: "analytics",
    icon: BarChart3,
    title: "Benchmarking & Intelligence",
    category: "analysis",
    content: `**Benchmarking (Directors only)**
Three analysis views:
1. **Project Records** — All projects with estimated vs actual hours, expanding to show role-level detail
2. **Analytics** — Sector averages, size-based benchmarks (fee per sqm), and variance reports
3. **Fee Prediction** — Filter by sector, type, and floor area to see percentile fee ranges from historical data

**Pricing Intelligence (Directors only)**
Dashboard with KPI cards:
- Total priced projects and pipeline value
- Average margin percentage
- Average hours per project
- Projects with actuals recorded vs estimated
- Hours variance (actual vs estimated)
- High risk project count
- Low confidence project count

Two sub-tabs:
- **KPIs & Charts** — Visual dashboard with summary statistics and trend data
- **Pricing Accuracy** — Detailed comparison of estimated vs actual hours across projects, with variance analysis

**Health Score**
The RAG (Red/Amber/Green) indicator evaluates four factors:

1. **Confidence Level** — Based on the average confidence score (drawings, specs, surveys, programme)
   - Score 0 (Green): average >= 3.5 (High confidence)
   - Score 1 (Amber): average >= 2.0 (Medium confidence)
   - Score 2 (Red): average < 2.0 (Low confidence)
2. **Discount** — Whether the discount percentage is within safe limits
   - Score 0: discount <= 5%
   - Score 1: discount <= threshold (default 15%, configurable in Admin)
   - Score 2: discount > threshold
3. **Risk Hours** — Risk hours as a percentage of total base hours
   - Score 0: risk ratio <= 5%
   - Score 1: risk ratio <= 15%
   - Score 2: risk ratio > 15%
4. **Benchmark** — Whether the fee as a percentage of construction value is within the expected range
   - Score 0: between 2% and 8% (or no construction value entered)
   - Score 1: between 1-2% or 8-12%
   - Score 2: outside 1-12%

Each factor scores 0 (good), 1 (warning), or 2 (concern).

**The overall health score is determined by the worst single factor, not the sum.** If any one factor scores 2, the overall status is Red. If the worst factor is 1, it is Amber. Only if all four factors score 0 is the status Green.

- **Green** — All factors healthy
- **Amber** — At least one factor at warning level
- **Red** — At least one factor at concern level

Hover over the health score indicator to see the breakdown of each factor.`
  },
  {
    id: "calibration",
    icon: TrendingUp,
    title: "Calibration Engine",
    category: "analysis",
    content: `The Calibration Engine analyses historical project data to generate pricing model adjustments. It compares estimated hours versus actual hours across completed projects and produces recommended multiplier changes.

**How It Works**
1. Projects must be marked as **Closed** with approved timesheets recording actual hours
2. The engine compares estimated hours to actual hours by sector and project type
3. It calculates a recommended complexity multiplier adjustment
4. Directors can review the recommendations and apply them to admin settings

**Enabling Calibration**
Go to Admin > General Settings. The Calibration Engine has an on/off toggle. When enabled, the system periodically analyses closed projects and generates recommendations.

**Viewing Recommendations**
Navigate to the Pricing Intelligence page. Calibration recommendations appear as suggested adjustments:
- Which sector/project type combination needs adjustment
- Current multiplier vs recommended multiplier
- Number of data points (projects) behind each recommendation
- Confidence level of the recommendation

**Applying Recommendations**
Recommendations are advisory only. Directors can choose to:
- Apply a recommendation to update the default complexity factor for that sector/type
- Dismiss a recommendation if it does not align with business judgment
- Wait for more data points before acting

**Data Requirements**
The calibration engine requires at least 3 closed projects with actual hours recorded per sector/type combination before generating a recommendation. Fewer data points produce low-confidence suggestions.

**Why It Matters**
Over time, the calibration engine automatically identifies systematic pricing biases. If your consultancy consistently underestimates residential projects, the engine will recommend increasing the complexity multiplier for that sector — leading to more accurate quotes and healthier margins.`
  },
  {
    id: "contractor-dashboard",
    icon: Users,
    title: "Contractor Performance Dashboard",
    category: "analysis",
    content: `The Contractor Performance Dashboard (Directors only) provides a centralised view of all contractor and sub-consultant engagements across every project.

**Summary KPIs**
At the top of the dashboard:
- Total engagements (how many contractor appointments across all projects)
- Unique contractors (distinct contractor names)
- Total quoted (sum of all contractor costs)
- Total actual (sum of actual contractor costs where recorded)
- Overall variance (difference between quoted and actual)

**Spend Chart**
A visual breakdown of contractor spend by contractor name, showing quoted vs actual costs side by side.

**Searchable Table**
A detailed table listing every contractor engagement with:
- Contractor name
- Project name and client
- Quoted cost
- Actual cost (if recorded)
- Client fee
- Variance (actual minus quoted)
- Variance percentage

The table is searchable and sortable by any column.

**Variance Tracking**
Once a project is closed and actual contractor costs are known, you can record the actual cost against each contractor. The dashboard then calculates variance — positive means over budget, negative means under budget. This data feeds into future contractor cost estimation.`
  },
  {
    id: "exports",
    icon: HardDriveDownload,
    title: "Data Exports",
    category: "analysis",
    content: `The system supports several export formats for reporting and analysis.

**Excel Exports**
1. **Benchmarking Export** — From the Benchmarking page, export all project data as an Excel workbook. Includes: project name, client, sector, type, status, total fee, estimated hours, construction value, fee percentage, disciplines, and creation date.

2. **Project Detail Export** — From any project's Pricing tab, export a detailed Excel workbook with three sheets:
   - **Overview** — Project metadata and pricing summary
   - **Scope Items** — All deliverables with hours by role
   - **Fee Breakdown** — Fee by stage and by discipline

**CSV Exports**
- **Intelligence Data** — Export pricing intelligence KPIs and variance analysis as CSV
- **Stage Data** — Export stage-level fee and hours data as CSV

**How to Export**
Each export page has a download button (usually in the top-right corner). Click it to generate and download the file immediately. Large exports may take a few seconds to generate.

**Use Cases**
- Import into internal reporting tools or business intelligence platforms
- Share pricing data with senior management or stakeholders who do not have system access
- Archive completed project data for external record-keeping
- Cross-reference with accounting or project management software`
  },
  /* ━━━ SECURITY & SESSIONS ━━━ */
  {
    id: "security-overview",
    icon: KeyRound,
    title: "Authentication & Security",
    category: "security",
    content: `**Login Methods**
The system supports two authentication methods:

1. **Email & Password** — Standard login with email and password. Passwords are hashed with bcrypt. JWT tokens are stored as httpOnly cookies (cannot be accessed by JavaScript), with a Bearer header fallback for API clients.

2. **Passkeys (WebAuthn)** — Biometric login using Face ID, Touch ID, or fingerprint. Register a passkey from My Profile, then use it to log in without a password. Passkeys are tied to your device and browser.

**Forgot Password**
If you forget your password:
1. Click "Forgot password?" on the login screen
2. Enter your email address
3. If email is configured, you receive a branded reset link (valid for 2 hours)
4. Click the link and enter a new password
5. Your old password is immediately replaced

**Note:** If SMTP/Resend email is not configured by the Director, the forgot password feature will show a message to contact your administrator.

**Login Rate Limiting**
The login endpoint is rate-limited to prevent brute-force attacks. Excessive login attempts from a single IP address will be temporarily blocked.

**CORS**
The API only accepts requests from configured origins (the frontend URL). This prevents unauthorised cross-origin requests.

**JWT Token Security**
- Tokens expire after 7 days
- Tokens are stored as httpOnly cookies (not accessible to JavaScript)
- The JWT secret is unique per deployment (auto-generated if not set)
- Tokens are invalidated when the user changes their password`
  },
  {
    id: "sessions",
    icon: MonitorSmartphone,
    title: "Active Sessions & Device Management",
    category: "security",
    content: `The system tracks all active login sessions across your devices.

**Viewing Active Sessions**
Go to My Profile and scroll to the "Active Sessions" section. You will see a list of all devices currently logged into your account, including:
- Browser name and version
- Operating system
- IP address
- Last activity timestamp
- Current device indicator (highlighted)

**Remote Logout**
You can log out any specific session by clicking the "Logout" button next to it. This immediately invalidates that session's token. Use this if:
- You left yourself logged in on a shared computer
- You see an unrecognised device in your session list
- You want to reduce your active sessions

**Logout All Others**
Click "Logout All Others" to invalidate every session except the one you are currently using. This is useful if you suspect your credentials may have been compromised.

**Session Details**
Sessions are identified by:
- A unique session ID
- User agent string (browser + OS detection)
- IP address at the time of login
- Creation timestamp
- Last activity timestamp`
  },
  {
    id: "idle-timeout",
    icon: Timer,
    title: "Idle Timeout & Auto-Save",
    category: "security",
    content: `The system includes an inactivity timeout to protect sensitive pricing data on unattended devices.

**How Idle Timeout Works**
1. After a configurable period of inactivity (default: 15 minutes), a warning modal appears
2. The modal shows a 60-second countdown with a "Stay Logged In" button
3. If you click "Stay Logged In", the timer resets and you continue working
4. If you do not respond within 60 seconds, you are automatically logged out

**Activity Detection**
The system monitors mouse movement, keyboard input, clicks, scroll, and touch events. Any of these resets the idle timer.

**Auto-Save**
When enabled, the system automatically saves your current work every 5 minutes (configurable). This means if you are logged out by the idle timeout, your most recent changes are preserved.

**Configuration (Directors Only)**
In Admin > System Settings:
- **Idle Timeout** — Set the inactivity period (5 to 20 minutes)
- **Auto-Save Toggle** — Enable or disable auto-save
- **Auto-Save Interval** — Set the interval (in minutes)

**Best Practice**
For consultancies handling sensitive client data, a shorter timeout (5-10 minutes) is recommended. For solo users on private devices, a longer timeout (15-20 minutes) provides a better user experience.`
  },
  /* ━━━ CONFIGURATION ━━━ */
  {
    id: "admin",
    icon: Wrench,
    title: "Admin Settings — All 12 Tabs",
    category: "configuration",
    content: `The Admin Settings page has 12 configuration tabs. Only Directors have access to this page.

**1. Charge Rates — Internal Roles**
Set the charge rate (what you bill the client per hour) and cost rate (your internal cost per hour) for each role. These rates are the foundation of all pricing calculations. Changes here affect all future price calculations — existing saved calculations are not retroactively changed.

Adding New Roles: Directors can add custom roles (e.g. "Graduate Engineer", "BIM Coordinator") using the "Add Role" row at the bottom of the rates table. New roles immediately become available in Scope Builder, Pricing Calculator, Proposals, and Timesheets.

Archiving Roles: Click the eye icon to archive a role. Archived roles are hidden from new entries but remain visible on historical projects.

Deleting Roles: Click the trash icon to permanently delete a role. A confirmation warns that historical data may be affected.

**Contractor Library:** A reusable directory of contractors and sub-consultants (e.g. "Acoustic Consultant", "Fire Engineer"). Each entry has a name, default rate, and default cost rate. When adding contractors to a project, you can select from this library to pre-fill details.

**2. Deliverables Library**
The master list of deliverables available to assign to projects. Each deliverable has a name, discipline, stage, description, and default hours per role. You can add deliverables manually or import via CSV.

**3. Templates**
Pre-configured project setups that can be applied during project creation. Templates store sector, project type, disciplines, and stage framework settings.

**4. Stage Frameworks**
Define the stages used to organise deliverables. RIBA stages (Stage 0-7) are the default. BSRIA (Phase 1-6) is also available. You can create custom frameworks and add or rename stages within each.

**5. General Settings**
Core configuration values:
- **Benchmark Target %** — Target MEP fee as % of construction value (typically 3-5%)
- **Default Complexity Factor** — Default multiplier for new projects (typically 1.0)
- **Discount Threshold %** — Discounts above this trigger a health score warning
- **Hours per Day** — Conversion rate for the hours/days toggle (default 8)
- **Procurement Route Effects** — Multiplier per procurement route
- **Programme Duration Multipliers** — Normal, Compressed, Accelerated, Extended
- **Size Scaling Rules** — Configurable floor area thresholds and bonus coefficients
- **Effort Drivers** — Compound multipliers (BIM, Heritage, BREEAM, etc.) with active/inactive toggles
- **Confidence Contingency** — Fee uplift percentages per confidence band (Low, Medium, High). Default: Low=10%, Medium=5%, High=0%.
- **Currency** — Select from 17 supported currencies

**6. Team Management**
View all user accounts, change roles, reset passwords, invite new users, and remove accounts. Set permission tiers and project creation rights per user.

**7. Theme**
Set your personal theme preference: Dark, Light, or Colourful. Directors can also set the platform default theme for new users.

**8. Branding**
Configure white-label branding for the entire platform: platform name, company name, logo, favicon, and login screen text. All branding changes apply across the entire platform including generated proposals.

**9. System**
System-level controls including:
- **Idle Timeout** settings (5-20 minutes)
- **Auto-Save** toggle and interval
- **Database Cleanup** — Remove orphaned records left behind by previously deleted projects (pricing requests, snapshots, timesheets, etc. that reference non-existent projects). Safe and non-destructive to active data.
- **Reset Audit Trail** — Clears all audit records. Does not affect project data.
- **Reset Pricing History** — Clears pricing snapshots, benchmark records, and calibration data.
- **Reset All Project Data** — Permanently deletes ALL projects, scope, pricing, proposals, risk assessments, timesheets, pricing history, and notifications. User accounts and admin settings are preserved.

Each reset requires confirmation and is logged.

**10. Data Protection**
GDPR compliance settings: data retention period configuration, right to deletion controls, data export functionality for compliance requests.

**11. Email**
Configure outbound email for notifications. Supports two providers:
- **SMTP** — Traditional SMTP server (host, port, username, password, TLS)
- **Resend** — Modern email API (API key + from address)

Switch between providers with one click. Configure which of the 6 event types trigger emails. Includes send log, template preview, and test email functionality. See the "Email System" section for full details.

**12. Audit Trail**
View the complete system audit trail showing all significant actions: scope changes, pricing overrides, approval decisions, proposal revisions, user management events, and system events. Filter by date range or search by description.`
  },
  {
    id: "email-system",
    icon: Mail,
    title: "Email System & Delivery Monitoring",
    category: "configuration",
    content: `The email system sends automated notifications for key events and provides a full delivery monitoring dashboard.

**Two Providers**
The system supports two email providers, configurable in Admin > Email:

1. **SMTP** — Connect to any SMTP mail server. Configure host, port, username, password, and TLS settings. Suitable for organisations with their own mail infrastructure.

2. **Resend** — Modern email API service. Simply enter your Resend API key and from address. Supports webhook integration for delivery tracking.

Switch between providers with a single click in the provider toggle. Both providers use the same branded HTML email templates.

**6 Event Types**
Each event type can be individually enabled or disabled:
1. **Approval Status Changes** — When a project moves between approval statuses
2. **Project Team Assignment** — When a user is added to a project team
3. **Proposal Issued** — When a proposal is marked as Issued to client
4. **New User Invitation** — When a new user is invited to the platform
5. **Timesheet Submitted** — When a team member submits a weekly timesheet for review
6. **Timesheet Approved/Rejected** — When a submitted timesheet is approved or rejected

**Delivery Monitoring Dashboard**
The Email tab in Admin provides a comprehensive monitoring view:
- **Success rate** — Percentage of emails successfully delivered vs failed
- **14-day timeline** — Chart showing email volume and success/failure over time
- **Failure breakdown** — Categorised view of failure reasons
- **Recent send log** — Detailed list of all sent emails with recipient, subject, status, and timestamp
- **Retry** — Failed emails can be retried directly from the send log

**Resend Webhooks**
When using Resend, you can enable webhook tracking with one click:
- The system registers a webhook endpoint with Resend
- Delivery events (delivered, bounced, opened, clicked) are received in real-time
- Events appear in the webhook events log within Admin > Email
- This provides visibility into whether clients actually received and opened your emails

**Test Email**
Click "Send Test Email" to verify your configuration is working. A branded test email is sent to the logged-in user's email address.

**Template Preview**
Preview any email template (approval, invitation, timesheet, etc.) without sending it. This helps verify branding and content before enabling notifications.`
  },
  {
    id: "tiers",
    icon: Layers,
    title: "Permission Tiers",
    category: "configuration",
    content: `The Permission Tiers system controls what each user can see and do within a project. There are 5 tiers, assigned per user by the Director via Admin > Team Management.

**Tier 1 — Timesheet Only (Default)**
The default tier for all non-Director users when first created.
- Access: Timesheets tab only
- Cannot view project overview, scope, pricing, risk, approval, proposals, or files
- Suitable for: Junior staff who only need to log their time against projects

**Tier 2 — Contributor**
Adds scope editing and file access.
- Access: Timesheets + Scope + Files tabs
- Can edit scope hours and deliverables on assigned projects
- Can upload and download project files
- Cannot view pricing details, risk assessments, or approval status
- Suitable for: Engineers actively working on deliverables who need to update hours and reference documents

**Tier 3 — Project Lead**
Adds project management capabilities.
- Access: Timesheets + Scope + Files + Overview + Risk + Pricing (excluding margin) + Approval (submit only)
- Can view the project overview and edit project details
- Can view and edit risk assessments and confidence scores
- Can view pricing summary but cannot see internal cost, margin, or margin percentage
- Can submit projects for review (Draft to Ready for Review) but cannot approve or reject
- Suitable for: Senior Engineers or Project Leads managing the pricing process

**Tier 4 — Senior Reviewer**
Adds internal pricing visibility and approval authority.
- Access: Everything in Tier 3 + Internal pricing (cost, margin, margin %) + Proposals + Approval (approve/reject)
- Can see full internal pricing details including cost rates, margins, and health scores
- Can generate, edit, and download proposals
- Can approve or reject projects in the approval workflow
- Can access the Approvals queue from the navigation bar
- Suitable for: Associates or Senior Engineers with review responsibility

**Tier 5 — Director (Automatic)**
Full unrestricted access to every feature.
- Access: All project tabs + Admin Settings + Benchmarking + Pricing Intelligence + Contractor Dashboard
- Can manage all admin configuration (rates, templates, branding, team, etc.)
- Can force-approve projects at any stage
- All users with the "Director" role are automatically Tier 5 — this cannot be changed

**Changing a User's Tier:**
1. Go to Admin > Team Management
2. Find the user in the table
3. Select the new tier from the dropdown (Tier 1 through Tier 4; Tier 5 is automatic for Directors)
4. The change takes effect immediately — no logout or refresh required

**"Can Create Projects" Permission:**
This is a separate toggle on each user, independent of their tier. When enabled, the user sees the "+ New" button and can access /projects/new. When disabled, attempting to navigate to /projects/new redirects to the Dashboard. Directors always have this permission.`
  },
  {
    id: "currency",
    icon: Globe,
    title: "Currency Configuration",
    category: "configuration",
    content: `The platform supports 17 currencies for fee presentation. The configured currency symbol appears throughout the application — in pricing summaries, dashboard KPIs, proposals, and benchmarking.

**Supported Currencies:**
GBP (British Pound) — default, USD (US Dollar), EUR (Euro), AUD (Australian Dollar), CAD (Canadian Dollar), CHF (Swiss Franc), JPY (Japanese Yen), CNY (Chinese Yuan), INR (Indian Rupee), AED (UAE Dirham), SGD (Singapore Dollar), HKD (Hong Kong Dollar), NZD (New Zealand Dollar), ZAR (South African Rand), SEK (Swedish Krona), NOK (Norwegian Krone), DKK (Danish Krone)

**Changing Currency:**
Go to Admin > General Settings and select the desired currency code. The change applies immediately across all screens and generated proposals.

**Important Notes:**
- The currency setting affects display only — it does not convert fees between currencies
- All charge rates, cost rates, and fees are stored in the same currency
- When changing currency, ensure your charge rates and cost rates are also updated to reflect the correct values in the new currency
- Historical project data will display in the current currency setting, not the currency that was active when the project was priced`
  },
  /* ━━━ SECURITY (cont) ━━━ */
  {
    id: "profile",
    icon: UserCircle,
    title: "My Profile & Push Notifications",
    category: "security",
    content: `Access your profile from the user menu in the top navigation bar.

**Edit Profile**
Update your display name and email address. Changing your email will update your login credentials — you will use the new email to sign in.

**Change Password**
Enter your current password and a new password (minimum 6 characters). Password changes take effect immediately.

**Register Passkey**
Set up biometric login (Face ID, Touch ID, fingerprint) from the Passkeys section. Once registered, you can use your passkey to log in without typing your password. You can register multiple passkeys on different devices. See the "Authentication & Security" section for details.

**Push Notifications**
Push notifications alert you when project approval statuses change. They work on tablets, iPads, and desktop browsers.

**Enabling Push Notifications:**
1. Go to My Profile
2. Click "Enable" in the Push Notifications section
3. Your browser will ask for notification permission — click "Allow"
4. Once enabled, you will receive browser push alerts for approval status changes, even when the app tab is not in focus

**Managing Push Notifications:**
- View how many devices are subscribed (you can enable on multiple devices)
- Click "Disable" to unsubscribe the current device
- Send a "Test Notification" to verify push is working on your device

**When notifications are blocked:**
If you previously blocked notification permission in your browser, the profile page will show a message explaining how to re-enable it in your browser or device settings.

**What triggers push notifications:**
- Project submitted for review
- Project approved
- Project rejected
- Revision requested
- Proposal issued`
  },
  {
    id: "offline",
    icon: Wifi,
    title: "PWA & Offline Mode",
    category: "security",
    content: `The MEP Pricing Engine is a Progressive Web App (PWA), meaning it can be installed on your device and used offline.

**Installing the App**
In most browsers (Chrome, Edge, Safari 16.4+), you will see an install icon in the address bar or a prompt to "Add to Home Screen". Click it to install the app:
- On desktop: Installs as a standalone window (no browser chrome)
- On tablet/iPad: Adds to the home screen as an app icon

Once installed, the app launches like a native application.

**Offline Capabilities**
When your device loses internet connectivity:
- A yellow "You are offline" banner appears at the top of the screen
- Previously loaded data (project lists, scope, pricing) is served from the local IndexedDB cache
- You can continue browsing cached data and making changes

**Offline Writes**
Changes made while offline are queued in a local write queue:
- The banner shows the count of pending changes (e.g. "2 pending changes")
- When connectivity resumes, the system automatically syncs all pending changes to the server
- You will see a "Back online — Syncing pending changes" notification
- Once sync completes, all data is up to date

**What works offline:**
- Viewing cached projects, scope, pricing, and risk data
- Making edits to scope hours, pricing factors, risk scores
- Logging timesheet entries
- Browsing the User Manual

**What requires connectivity:**
- Creating new projects
- Generating proposals (PDF/Word)
- Uploading files
- Sending approval status changes
- Admin configuration changes

**Data Freshness**
Cached data is updated each time you view a page while online. If data has not been cached for a particular project, it will not be available offline. For best offline experience, open your active projects while connected to ensure the cache is populated.`
  },
  /* ━━━ TECHNICAL REFERENCE ━━━ */
  {
    id: "formulas",
    icon: AlertTriangle,
    title: "Formula Reference",
    category: "reference",
    content: `**Complete Formula Summary**

**Base Fee Calculation:**
Base Fee = Sum across all roles of (Role Hours x Role Charge Rate)
Note: Risk additional hours are distributed (40% Senior Engineer, 60% Engineer) and included in the role hours before this calculation.
Note: If a deliverable uses "days", it is converted: hours = days x hours_per_day before summing.

**Size Scaling (Threshold-Based):**
Floor area base multiplier (step lookup):
- Up to 1,000 sqm = 0.85
- Up to 5,000 sqm = 1.00
- Up to 15,000 sqm = 1.10
- Up to 50,000 sqm = 1.20
- Over 50,000 sqm = 1.35

Additive bonuses (above minimum thresholds only):
- Floors > 5: (floors - 5) x 0.02
- Residential units > 20: (units - 20) x 0.005
- Plant rooms > 2: (plant_rooms - 2) x 0.03
- Risers > 2: (risers - 2) x 0.01
- Technical systems > 5: (systems - 5) x 0.02

Size Multiplier = floor area base + sum of applicable bonuses
All values are admin-configurable via size_scaling_rules.

**Combined Multiplier:**
Combined = Complexity x Coordination x Size Scaling x Procurement Route x Programme Duration x Effort Drivers
(All six factors are multiplied together. Effort Driver Multiplier = product of all active individual effort driver multipliers.)

**Final Fee:**
Adjusted Fee = Base Fee x Combined Multiplier
Confidence Contingency = Adjusted Fee x Contingency% (Low=10%, Medium=5%, High=0%)
Fee After Contingency = Adjusted Fee + Confidence Contingency
Discount = Fee After Contingency x (Discount% / 100)
Final Fee = Fee After Contingency - Discount
(If fee rounding is set, Final Fee is rounded up to the nearest specified increment using ceiling function.)

**Discount Governance:**
If Discount% > admin threshold (default 15%), the project is auto-flagged for approval and the health score reflects a warning.

**Optional Services:**
Optional Service Fee = Sum of (role hours x role charge rate) x Combined Multiplier per optional item
Optional services are priced separately and not included in the base fee total.

**Contractor / Sub-Consultant Pricing:**
When markup is enabled: Client Fee = Contractor Cost x (1 + Markup% / 100)
When markup is off: Client Fee = Contractor Cost
Project Total (incl. Contractors) = Final Fee + Sum of Contractor Client Fees

**Margin (Internal — Directors Only):**
Adjusted Cost = Base Cost x Combined Multiplier
Margin = Final Fee - Adjusted Cost
Margin % = (Margin / Final Fee) x 100

**Benchmark:**
Benchmark % = (Final Fee / Construction Value) x 100

**PERT Estimate:**
PERT = (Optimistic + 4 x Most Likely + Pessimistic) / 6
Std Dev = (Pessimistic - Optimistic) / 6

**Risk Hours:**
Risk Hours = Sum of all Impact Hours across all risk items (straight sum — probability is metadata only, not a weighting factor).
Risk hours are then allocated: 40% to Senior Engineer, 60% to Engineer.

**Confidence Band:**
Average = (Drawings + Specifications + Surveys + Programme) / 4
High: average >= 3.5
Medium: average >= 2.0
Low: average < 2.0

**Health Score (Worst-Factor Model):**
Four factors, each scored 0 (good), 1 (warning), 2 (concern):
- Confidence: High=0, Medium=1, Low=2
- Discount: <=5%=0, <=threshold=1, >threshold=2
- Risk ratio: <=5%=0, <=15%=1, >15%=2
- Benchmark: 2-8%=0, 1-12%=1, outside=2

Overall = max(all four factor scores)
Green: max=0, Amber: max=1, Red: max=2
(Any single Red factor makes the entire score Red.)

**Scenario Comparison:**
Each scenario applies its own set of multipliers independently. The base hours remain the same — only the multiplier factors and discount change per scenario.`
  },
  {
    id: "manual-overrides",
    icon: Pencil,
    title: "Manual Fee Overrides",
    category: "core-workflow",
    content: `Manual Fee Overrides allow you to lock in a specific fee amount at different levels of your pricing, bypassing the calculated result.

**When to use overrides**
Use overrides when the calculated fee needs to match a pre-agreed figure, a budget cap, or a commercial target — without changing the underlying scope hours.

**Override Levels**
Three override levels are available in the Pricing tab:

1. **Total Project Override** — Sets the entire project fee to a fixed amount. When active, all calculated stage and discipline breakdowns are replaced with proportional allocations based on the overridden total.

2. **Stage Override** — Locks a specific stage (e.g. Stage 2 Concept Design) to a fixed fee. Other stages continue to use calculated values. The total fee adjusts to reflect the override.

3. **Stage + Discipline Override** — The most granular level. Locks a specific stage-discipline combination (e.g. Stage 3 Mechanical) to a fixed fee. Other cells in the matrix remain calculated.

**How to apply**
- Navigate to the Pricing tab after calculating your fee
- Look for the override icon next to the fee figure you want to override
- Enter your target fee amount
- The system recalculates totals immediately, showing the override with a visual indicator

**Override hierarchy**
If multiple override levels conflict, the most specific override wins:
Stage+Discipline > Stage > Total Project

**Visual indicators**
- Overridden values display a distinct badge or highlight so reviewers can see which figures are manually set vs calculated
- The pricing summary shows a count of active overrides

**Audit trail**
All overrides are recorded with the user who applied them and the timestamp, visible in the project audit log.`
  },
  {
    id: "granular-discounts",
    icon: TrendingUp,
    title: "Granular Discounts",
    category: "core-workflow",
    content: `Granular Discounts let you apply percentage or fixed-amount reductions at different levels of the fee breakdown.

**Discount Levels**
Three discount levels are available:

1. **Project-Level Discount** — Applies to the entire project fee. Enter a percentage (e.g. 10%) or a fixed amount (e.g. £5,000). This is the most common type, used for client-wide agreements or competitive adjustments.

2. **Stage-Level Discount** — Applies to a specific stage only. Useful when a client requests a reduced fee for early-stage advisory work while paying full rates for detailed design.

3. **Stage + Discipline Discount** — The most granular. Applies to a specific cell in the stage-discipline matrix. Use this for targeted negotiations (e.g. 15% discount on Stage 4 Electrical only).

**How to apply**
- In the Pricing tab, locate the discount controls next to the fee figures
- Select the discount type (percentage or fixed amount)
- Enter the value
- The fee adjusts immediately, with the discount amount shown alongside

**Discount governance**
If Admin > General Settings has a discount threshold configured (e.g. 15%), any discount exceeding that threshold automatically flags the project for Director approval. The project status changes to reflect that a governance review is required.

**Stacking behaviour**
Discounts at different levels stack. If a project has a 5% project-level discount and a 10% stage-level discount on Stage 3, Stage 3 receives both discounts applied sequentially. The calculation is:
Stage 3 Fee = (Calculated Stage 3 Fee × 0.90) then × 0.95

**Overrides vs Discounts**
- **Overrides** replace the calculated fee with a fixed number
- **Discounts** reduce the calculated fee by a percentage or amount
- Both can be active simultaneously. Overrides are applied first, then discounts are applied to the overridden value.`
  },
  {
    id: "technical-systems",
    icon: Wrench,
    title: "Technical Systems Configuration",
    category: "configuration",
    content: `Technical Systems define the MEP system types present in a project. This affects the size scaling multiplier in pricing calculations.

**What are Technical Systems?**
Each MEP project involves specific engineering systems such as Heating, Cooling, Ventilation, LV Distribution, Fire Alarm, Domestic Water, etc. The number and type of systems affects project complexity — more systems means more coordination, more drawings, and more engineering hours.

**Admin Setup**
Directors configure the available systems in Admin > Tech Systems tab:
- Systems are grouped by discipline: Mechanical, Electrical, Public Health
- Each system can be toggled active/inactive
- New systems can be added with a name and group assignment
- Systems can be deleted if no longer applicable

**Default Systems (pre-seeded)**
The platform ships with a comprehensive default library:
- **Mechanical**: Heating, Cooling, Ventilation, Air Conditioning, VRF/VRV, Chilled Water, LTHW Heating, Heat Pumps, Domestic Water Services, Gas Services, BMS/Controls, Smoke Control
- **Electrical**: LV Distribution, Small Power, Lighting, Fire Alarm, Security, Data/Comms, Lightning Protection, Emergency Lighting, Generator/UPS, EV Charging, PV Solar, Nurse Call, Access Control, CCTV, Disabled Refuge, Public Address
- **Public Health**: Above Ground Drainage, Below Ground Drainage, Domestic Hot/Cold Water, Rainwater, Sanitary Ware, Water Treatment, Sprinklers, Gas, Grease Traps, Trade Waste

**Project Selection**
When setting up a project (Overview tab > Size Metrics), you select which technical systems are relevant from the admin-curated list. The selection uses a multi-select dropdown filtered by the project's active disciplines.

**Pricing Impact**
The count of selected technical systems feeds into the Size Scaling multiplier. If the number exceeds the configured threshold (default: 5 systems), a bonus is applied per additional system:

Technical Systems Bonus = (Systems Count - Threshold) × Bonus Rate (default 2% per system)

This bonus is added to the overall size scaling multiplier, increasing the project fee proportionally.`
  },
  {
    id: "sub-deliverables",
    icon: Layers,
    title: "Sub-Deliverables (Nested Items)",
    category: "core-workflow",
    content: `Sub-deliverables let you break down a main deliverable into finer-grained line items for better scope clarity.

**What are Sub-Deliverables?**
A main deliverable like "Mechanical Detailed Design Drawings" can have sub-items such as:
- Floor Plans
- Sections & Details
- Schematic Diagrams
- Equipment Schedules

These appear nested under the parent item in the Scope tab and are included in proposals for detailed scope breakdowns.

**How to add sub-items**
1. In the Scope tab, find the deliverable you want to expand
2. Click the expand/sub-item button on the row
3. A new sub-item row appears underneath
4. Type the sub-item name and press Enter or click away to save

**Managing sub-items**
- **Edit**: Click on any sub-item name to rename it
- **Reorder**: Use the up/down arrows to change the display order
- **Delete**: Click the delete icon to remove a sub-item

**Important notes**
- Sub-items are descriptive — they provide scope clarity but do not carry their own hour allocations. Hours are entered at the parent deliverable level.
- Sub-items are included in CSV exports (as a "sub_deliverable" column)
- Sub-items appear in generated proposals under their parent deliverable
- There is no limit to the number of sub-items per deliverable

**CSV Import with Sub-items**
When importing via CSV, include a "sub_deliverable" column. Rows sharing the same stage, discipline, and deliverable name will be grouped, with distinct sub_deliverable values becoming nested sub-items.`
  },
  {
    id: "csv-scope-tools",
    icon: HardDriveDownload,
    title: "CSV Scope Import, Export & Templates",
    category: "core-workflow",
    content: `The Scope tab includes powerful CSV tools for importing, exporting, and managing scope data across projects.

**CSV Export**
Click "More Actions > CSV Export" to download your current scope as a CSV file. The export includes:
- Stage, Discipline, Deliverable name, Sub-deliverable, Classification, and Quantity
- File is named with the project ID and current date for easy tracking
- Useful for offline review, sharing with colleagues, or as a backup before major changes

**CSV Import**
Click "More Actions > CSV Import" to upload scope from a CSV file or paste CSV text directly:

1. **Paste or upload** your CSV data into the import dialog
2. **Preview** — the system parses and shows you the rows with any errors highlighted
3. **Choose import mode**:
   - **Replace** — clears existing scope and imports the CSV data fresh
   - **Merge** — adds new deliverables from the CSV without removing existing ones
4. **Confirm** — items are created in the project scope immediately

**CSV Format**
Required columns: stage, discipline, deliverable
Optional columns: sub_deliverable, classification, quantity

Example:
stage,discipline,deliverable,sub_deliverable,classification,quantity
"Stage 2","Mechanical","Heating Design Drawings","Floor Plans","Default",1
"Stage 2","Mechanical","Heating Design Drawings","Sections","Default",1
"Stage 3","Electrical","Lighting Layout","","Default",1

**Save as Template**
Click "More Actions > Save as Template" to save your current scope configuration as a reusable template:
- Give it a name and optional description
- Templates are stored server-side and available for future projects
- Templates capture all deliverables, sub-items, exclusions, and assumptions

**Load Saved Template**
Click "More Actions > Load Saved Template" to apply a previously saved scope template:
- **Replace mode** — replaces your entire current scope with the template
- **Merge mode** — shows a side-by-side comparison preview before merging:
  - Items only in your current scope (kept)
  - Items only in the template (will be added)
  - Items in both (unchanged)
  - Total item count after merge

The merge preview helps you make an informed decision before combining scope data from different sources.`
  },
  {
    id: "staff-hours-csv",
    icon: HardDriveDownload,
    title: "Staff Hours CSV Import & Export",
    category: "core-workflow",
    content: `The Scope tab provides a dedicated CSV workflow for managing staff hours in bulk — useful when hours are prepared offline or shared across team members.

**Download Hours Template**
From the Scope tab toolbar, click "More Actions > Download Hours CSV":
- Generates a CSV pre-populated with all current deliverables and role columns (Director, Associate, Senior Engineer, Engineer, CAD/BIM Technician, plus any custom roles)
- Existing hours are included so you can review or adjust offline
- File format: deliverable_id, stage, discipline, name, then one column per active role

**Upload Hours CSV**
From the Scope tab toolbar, click "More Actions > Upload Hours CSV":
- Upload the completed CSV file to bulk-import hours against deliverables
- Hours are matched by deliverable ID — rows that don't match a current deliverable are skipped with a warning
- Existing hours are overwritten with the CSV values

**Workflow Example**
1. Build your scope deliverables in the app
2. Download the hours template CSV
3. Send to discipline leads to fill in their hours offline
4. Upload the completed CSV back to the project
5. Review the populated hours in the Scope tab and proceed to pricing

This approach is especially useful for large projects with many deliverables where filling hours one-by-one in the UI would be time-consuming.`
  },
  {
    id: "general-discipline",
    icon: Shield,
    title: "General / Cross-Discipline Items",
    category: "core-workflow",
    content: `"General" is a recognised discipline within the pricing engine, used for deliverables that are not specific to Mechanical, Electrical, or Public Health — such as project management, coordination meetings, site visits, or quality assurance activities.

**How it Works**
- When adding or editing a deliverable in the Scope tab, select "General" from the Discipline dropdown
- General deliverables appear in their own section in the fee breakdown — both in the discipline summary and the stage×discipline matrix
- Hours assigned to General deliverables are priced using the same charge rates as any other discipline
- General items are fully included in all pricing calculations: base fee, multipliers, discounts, and overrides

**Where "General" Appears**
- **Scope Tab** — General deliverables are listed alongside Mechanical, Electrical, and Public Health items
- **Pricing Tab — Fee by Discipline** — a "General" row shows its total fee and percentage contribution
- **Pricing Tab — Stage × Discipline Matrix** — General has its own column in the cross-tabulation
- **CSV Export/Import** — General is a valid discipline value in CSV files
- **Proposals** — General items are included in generated PDF/Word proposals

**When to Use General**
Use the General discipline for activities that span across all MEP disciplines:
- Project management and coordination
- Design review meetings
- Site surveys and inspections
- Quality assurance / checking
- BIM coordination (if not discipline-specific)
- Client presentations and reporting

This avoids artificially inflating a specific discipline's hours with non-discipline-specific effort.`
  },
  {
    id: "formula-version",
    icon: Shield,
    title: "Formula Version History",
    category: "reference",
    content: `**Pricing Engine Formula Version: v1.6**
Last Updated: 2026-09-09

Every pricing calculation records the formula version used. This ensures older projects reference the correct logic used at the time of pricing.

**Changelog**

**v1.6 — 2026-09-09**
- Added "General" as a recognised discipline — integrated into fee-by-discipline breakdown and stage×discipline matrix
- Added Staff Hours CSV template download — pre-populated with project deliverables and role columns
- Added Staff Hours CSV bulk upload — import hours from CSV directly into scope deliverables
- Migrated authentication to httpOnly cookie-based JWT for improved session security
- Performance improvements via memoised computations in admin and benchmarking views

**v1.5 — 2026-09-09**
- Added Manual Fee Overrides at three levels: Total Project, Stage, and Stage+Discipline
- Added Granular Discount system with percentage and fixed-amount options at Project, Stage, and Stage+Discipline levels
- Replaced free-text technical systems with Admin-managed multi-select dropdown
- Added Sub-deliverables — nestable line items under main deliverables for detailed scope breakdowns
- Added Custom Stage creation — define new stages beyond the standard framework
- Added CSV Import with Replace and Merge modes for scope data
- Added CSV Export for full scope download
- Added Scope Template Save/Load with merge comparison preview
- Scope toolbar reorganised into primary actions and "More Actions" dropdown for cleaner UI

**v1.4 — 2026-03-27**
- Added per-project staff levels — customise which engineering roles are active per project
- Added per-project custom charge/cost rate overrides — override admin rates for specific projects
- Added confidence contingency multiplier — Low confidence +10%, Medium +5%, High +0% fee uplift
- Added rate change impact preview — live fee impact shown in rate editor before saving
- Added proposal staleness indicator — flags existing proposals when rates change
- Added technical systems to size scaling calculation (bonus above threshold)
- Pricing snapshots now only triggered on explicit calculation and approval events
- Project deletion now cascades to all related data including pricing requests and timesheets
- Added database orphan cleanup endpoint for data integrity maintenance

**v1.3 — 2026-03-17**
- Added fee rounding option (ceiling to nearest configurable increment)
- Added optional services pricing — priced separately with combined multiplier applied
- Added contractor/sub-consultant pricing with configurable markup percentage
- Added hours/days toggle with per-deliverable effort unit conversion (configurable hours-per-day)
- Added discount governance — discounts exceeding admin threshold auto-flag project for approval
- Added scenario comparison — side-by-side what-if analysis with delta column
- Added per-stage and per-discipline fee contribution percentages
- Added contractor cost and client fee totals to pricing output

**v1.2 — 2026-03-08**
- Size scaling updated to threshold-based model with configurable rules
- Risk hours clarified as straight sum of impact hours (probability is metadata only)
- Health score changed to worst-factor model (max of 4 factors, not sum)
- Stage-based effort visibility added to pricing, benchmarking, and proposals
- Formula version tracking added to all pricing records

**v1.1 — 2026-03-07**
- Added programme duration multipliers (Normal, Compressed, Accelerated, Extended)
- Added procurement route effects with configurable multipliers
- Added effort drivers as compound multipliers (BIM Level 2, Heritage, BREEAM, etc.)
- Added BIM/CAD toggle to auto-exclude BIM effort drivers for 2D CAD projects

**v1.0 — 2026-03-06**
- Initial pricing engine release
- Effort-based pricing with 5 roles and charge rates
- Complexity, coordination, and programme duration factors
- Size scaling multiplier
- PERT three-point estimating
- Risk register with 5 categories
- Confidence scoring (0-5 scale, High/Medium/Low bands)
- Health score RAG indicator

**Governance**
Each time a formula changes, the version increments and a changelog entry is recorded. All pricing records store the formula version used at the time of calculation. This provides:
- A clear audit trail of pricing logic changes
- Easier debugging and validation
- Stronger governance for SaaS commercialisation
- Historical accuracy when reviewing older projects

The current formula version is displayed in the Pricing Summary header and recorded in each project's pricing data and benchmarking snapshots.`
  },
];

/* ── Component ── */
export default function UserManual() {
  const [searchTerm, setSearchTerm] = useState("");
  const [openSection, setOpenSection] = useState("overview");
  const [activeCategory, setActiveCategory] = useState(null);
  const sectionRefs = useRef({});

  // Auto-scroll to selected section
  useEffect(() => {
    if (openSection && !searchTerm && sectionRefs.current[openSection]) {
      setTimeout(() => {
        sectionRefs.current[openSection]?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }, [openSection, searchTerm]);

  const filtered = useMemo(() => {
    let result = SECTIONS;
    if (activeCategory) {
      result = result.filter(s => s.category === activeCategory);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s =>
        s.title.toLowerCase().includes(term) ||
        s.content.toLowerCase().includes(term)
      );
    }
    return result;
  }, [searchTerm, activeCategory]);

  const matchCount = searchTerm ? filtered.length : null;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="user-manual-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-foreground">User Manual</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Complete reference for every feature, calculation, and workflow — {SECTIONS.length} sections
          </p>
        </div>
        <Badge variant="outline" className="text-xs font-mono self-start sm:self-end" data-testid="manual-formula-badge">
          Formula v1.6
        </Badge>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search the manual... (e.g. 'discount governance', 'PERT', 'passkey')"
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); if (e.target.value) setActiveCategory(null); }}
          className="pl-10 pr-20"
          data-testid="manual-search"
        />
        {matchCount !== null && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {matchCount} result{matchCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2" data-testid="manual-categories">
        <button
          onClick={() => { setActiveCategory(null); setSearchTerm(""); }}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
            !activeCategory && !searchTerm
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
          }`}
          data-testid="manual-cat-all"
        >
          All ({SECTIONS.length})
        </button>
        {CATEGORIES.map(cat => {
          const count = SECTIONS.filter(s => s.category === cat.id).length;
          return (
            <button
              key={cat.id}
              onClick={() => { setActiveCategory(activeCategory === cat.id ? null : cat.id); setSearchTerm(""); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                activeCategory === cat.id
                  ? cat.color + " border-current"
                  : "bg-card text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
              }`}
              data-testid={`manual-cat-${cat.id}`}
            >
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Quick navigation pills */}
      <ScrollArea className="w-full">
        <div className="flex gap-1.5 pb-2 min-w-max" data-testid="manual-nav-pills">
          {filtered.map(s => {
            const Icon = s.icon;
            const isActive = openSection === s.id && !searchTerm;
            return (
              <button
                key={s.id}
                onClick={() => { setOpenSection(s.id); setSearchTerm(""); setActiveCategory(null); }}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-all border whitespace-nowrap ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-card/50 text-muted-foreground border-border/50 hover:border-primary/40 hover:text-foreground"
                }`}
                data-testid={`manual-nav-${s.id}`}
              >
                <Icon className="w-3 h-3 flex-shrink-0" />
                {s.title.length > 25 ? s.title.substring(0, 22) + "..." : s.title}
              </button>
            );
          })}
        </div>
      </ScrollArea>

      {/* Sections */}
      <div className="space-y-2.5">
        {filtered.map(section => {
          const Icon = section.icon;
          const isOpen = searchTerm ? true : openSection === section.id;
          const cat = CATEGORIES.find(c => c.id === section.category);
          return (
            <Card
              key={section.id}
              ref={el => sectionRefs.current[section.id] = el}
              className="overflow-hidden scroll-mt-20"
              data-testid={`manual-section-${section.id}`}
            >
              <button
                onClick={() => setOpenSection(isOpen && !searchTerm ? "" : section.id)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-primary" />
                  </div>
                  <div className="text-left">
                    <span className="font-heading text-sm font-semibold text-foreground block">{section.title}</span>
                    {cat && (
                      <span className="text-[10px] text-muted-foreground">{cat.label}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isOpen
                    ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    : <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  }
                </div>
              </button>
              {isOpen && (
                <CardContent className="px-5 pb-5 pt-0 border-t border-border">
                  <div
                    className="prose prose-sm max-w-none text-muted-foreground mt-3 manual-content leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(formatMarkdown(section.content, searchTerm))
                    }}
                  />
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Footer */}
      <div className="text-center py-4 space-y-1">
        <p className="text-xs text-muted-foreground">
          {SECTIONS.length} sections covering every feature, formula, and workflow
        </p>
        <p className="text-[10px] text-muted-foreground/60">
          Last updated: September 2026 — Formula version: v1.6 — MEP .Engine by FURD Tech Ltd
        </p>
      </div>
    </div>
  );
}

function formatMarkdown(text, searchTerm) {
  let html = text
    .replace(/\*\*(.*?)\*\*/g, '<strong class="text-foreground font-semibold">$1</strong>')
    .replace(/^(\d+)\.\s/gm, '<span class="text-primary font-bold">$1.</span> ')
    .replace(/^- /gm, '<span class="text-primary mr-1 opacity-70">&#8226;</span>')
    .replace(/\n\n/g, '</p><p class="mt-3">')
    .replace(/\n/g, '<br/>')
    .replace(/^/, '<p>')
    .replace(/$/, '</p>');

  // Highlight search matches
  if (searchTerm && searchTerm.length > 1) {
    const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escaped})`, "gi");
    html = html.replace(regex, '<mark class="bg-yellow-500/30 text-foreground rounded px-0.5">$1</mark>');
  }

  return html;
}
